import { ArrowUpRight, Sparkles, TrendingUp } from "lucide-react";
import { waLink } from "@/lib/brand";
import AnimatedCounter from "./AnimatedCounter";

const STATS = [
  { num: 500, suffix: "+", label: "Projects Marketed Across India" },
  { num: 50, suffix: "+", label: "Builders, Developers & Channel Partners" },
  { num: 5, suffix: "L+", label: "Qualified Buyer Leads Delivered" },
  { num: 3.4, suffix: "x", label: "Average ROAS for Retainer Clients", float: true },
];

export default function Hero() {
  return (
    <section id="top" data-testid="hero-section" className="relative pt-32 pb-20 sm:pt-40 sm:pb-32 overflow-hidden">
      {/* Animated background layers */}
      <div className="absolute inset-0 bg-grid bg-grid-mask opacity-90" />
      <div className="absolute -top-32 -left-40 w-[600px] h-[600px] rounded-full bg-[#d4ff4f]/10 blur-[120px] blob" />
      <div className="absolute top-40 -right-32 w-[500px] h-[500px] rounded-full bg-emerald-500/10 blur-[120px] blob" style={{ animationDelay: "-6s" }} />
      <div className="absolute inset-0 noise" />

      <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8">
        {/* Top eyebrow */}
        <div className="flex flex-wrap items-center gap-3 mb-8 sm:mb-10" data-testid="hero-eyebrow">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#d4ff4f]/30 bg-[#d4ff4f]/5 font-mono text-[11px] uppercase tracking-[0.18em] text-[#d4ff4f]">
            <Sparkles className="w-3 h-3" />
            #1 Real Estate Marketing Agency · Delhi NCR
          </span>
          <span className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-white/40">
            <span className="w-2 h-2 rounded-full bg-[#d4ff4f] animate-pulse" />
            Live · Booking growth calls
          </span>
        </div>

        {/* Hero heading */}
        <h1 data-testid="hero-heading" className="font-display text-[44px] leading-[0.95] sm:text-[72px] md:text-[96px] lg:text-[120px] tracking-tighter">
          Built to <span className="italic text-[#d4ff4f]">book.</span>
          <br />
          Every flat. Every floor.
          <br />
          Every <span className="relative inline-block">
            <span className="relative z-10">plot.</span>
            <svg className="absolute -bottom-2 left-0 w-full h-3 text-[#d4ff4f]" viewBox="0 0 100 8" preserveAspectRatio="none">
              <path d="M0,4 Q25,0 50,4 T100,4" stroke="currentColor" strokeWidth="2" fill="none" />
            </svg>
          </span>
        </h1>

        <div className="grid lg:grid-cols-3 gap-8 mt-10 sm:mt-14">
          <div className="lg:col-span-2 max-w-3xl">
            <p className="text-base sm:text-lg text-white/70 leading-relaxed">
              <span className="text-white">Digi Savvy</span> is India's real-estate-only marketing engine. Brand → Demand → Leads → Bookings. Built for builders and channel partners who measure success in <span className="text-[#d4ff4f]">registrations, not reach.</span>
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-8">
              <a
                href={waLink("Hi Digi Savvy! I'd like to book a Growth Call.")}
                target="_blank"
                rel="noreferrer"
                data-testid="hero-cta-primary"
                className="group inline-flex items-center gap-2 bg-[#d4ff4f] text-black font-semibold px-6 py-3.5 rounded-full hover:bg-white transition glow-lime"
              >
                Book a Growth Call
                <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition" />
              </a>
              <a
                href="#verticals"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-2 border border-white/15 hover:border-[#d4ff4f]/50 text-white px-6 py-3.5 rounded-full hover:bg-white/5 transition"
              >
                Explore Verticals
                <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Floating card */}
          <div className="relative hidden lg:block">
            <div className="absolute -inset-1 bg-gradient-to-br from-[#d4ff4f]/30 to-transparent rounded-3xl blur-2xl" />
            <div className="relative rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-white/50">Live Dashboard</div>
                <div className="flex gap-1">
                  <span className="w-2 h-2 rounded-full bg-red-400" />
                  <span className="w-2 h-2 rounded-full bg-yellow-400" />
                  <span className="w-2 h-2 rounded-full bg-[#d4ff4f]" />
                </div>
              </div>
              <div className="text-4xl font-display tracking-tight">CPL <span className="text-[#d4ff4f]">₹389</span></div>
              <div className="text-xs text-white/50 mt-1">Builder Floors · Delhi NCR</div>
              <div className="mt-5 flex items-end gap-1 h-20">
                {[40, 60, 35, 80, 55, 92, 70, 95, 65, 100, 78, 110].map((h, i) => (
                  <div key={i} className="flex-1 rounded-t" style={{
                    height: `${h}%`,
                    background: `linear-gradient(180deg, #d4ff4f, ${i > 8 ? '#d4ff4f' : 'rgba(212,255,79,0.4)'} )`,
                  }} />
                ))}
              </div>
              <div className="mt-4 flex items-center justify-between text-xs">
                <span className="text-white/50">Last 12 weeks</span>
                <span className="text-[#d4ff4f] inline-flex items-center gap-1"><TrendingUp className="w-3 h-3" /> +47% MoM</span>
              </div>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-10 mt-16 sm:mt-24" data-testid="hero-stats">
          {STATS.map((s, i) => (
            <div key={i} className="group">
              <div className="font-display text-4xl sm:text-5xl md:text-6xl text-[#d4ff4f] tracking-tight">
                {s.float ? (
                  <span>{s.num.toString()}{s.suffix}</span>
                ) : (
                  <AnimatedCounter to={s.num} prefix={s.prefix || ""} suffix={s.suffix || ""} />
                )}
              </div>
              <div className="mt-2 h-px w-12 bg-[#d4ff4f] group-hover:w-24 transition-all" />
              <div className="mt-3 font-mono text-[10px] sm:text-xs uppercase tracking-wider text-white/60 leading-relaxed">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
